import { useCallback, useMemo } from 'react';
import { App } from 'antd';
import { trpc } from '../lib/api';
import { SystemConfigValueSchema, type SystemConfig } from '../../../shared/types/system-config.types';
import { getMutationPendingState } from '../utils/trpcMutation';

type ResolvedConfig = SystemConfig & { isDefault: boolean };
type SystemConfigValue = SystemConfig['configValue'];

const normalizeConfigValue = (value: unknown): SystemConfigValue => {
  const result = SystemConfigValueSchema.safeParse(value);
  return result.success ? result.data : '';
};

export const useSystemConfigs = () => {
  const query = trpc.systemConfig.listConfigs.useQuery(undefined, {
    refetchOnWindowFocus: false
  });

  return useMemo(
    () => ({
      configs: (query.data ?? []).map((config) => ({
        id: config.id ?? '',
        companyId: config.companyId ?? '',
        configKey: config.configKey ?? '',
        configValue: normalizeConfigValue(config.configValue),
        description: config.description ?? undefined,
        createdAt: config.createdAt ? new Date(config.createdAt) : new Date(0),
        updatedAt: config.updatedAt ? new Date(config.updatedAt) : new Date(0),
        isDefault: Boolean(config.isDefault),
      })) as ResolvedConfig[],
      isLoading: query.isLoading,
      refetch: query.refetch
    }),
    [query.data, query.isLoading, query.refetch]
  );
};

export const useSystemConfigMutations = () => {
  const { message } = App.useApp();
  const utils = trpc.useContext();

  const setMutation = trpc.systemConfig.setConfig.useMutation({
    onSuccess: () => {
      utils.systemConfig.listConfigs.invalidate();
      message.success('設定を保存しました');
    },
    onError: (error) => {
      message.error(error.message || '設定の保存に失敗しました');
    }
  });

  const deleteMutation = trpc.systemConfig.deleteConfig.useMutation({
    onSuccess: () => {
      utils.systemConfig.listConfigs.invalidate();
      message.success('設定を削除しました');
    },
    onError: (error) => {
      message.error(error.message || '設定の削除に失敗しました');
    }
  });

  const setConfig = useCallback(
    (configKey: string, value: unknown, description?: string | null) => {
      setMutation.mutate({
        configKey,
        configValue: normalizeConfigValue(value),
        description: description ?? undefined
      });
    },
    [setMutation]
  );

  const setConfigAsync = useCallback(
    (configKey: string, value: unknown, description?: string | null) =>
      setMutation.mutateAsync({
        configKey,
        configValue: normalizeConfigValue(value),
        description: description ?? undefined
      }),
    [setMutation]
  );

  const deleteConfig = useCallback(
    (configKey: string) => {
      deleteMutation.mutate({ configKey });
    },
    [deleteMutation]
  );

  const deleteConfigAsync = useCallback(
    (configKey: string) => deleteMutation.mutateAsync({ configKey }),
    [deleteMutation]
  );

  return {
    setConfig,
    setConfigAsync,
    deleteConfig,
    deleteConfigAsync,
    isSaving: getMutationPendingState(setMutation),
    isDeleting: getMutationPendingState(deleteMutation)
  };
};
